import {
  Box,
  Grid,
  Typography,
  Chip,
  CardContent,
  Card,
  Divider,
  Avatar,
  Stack,
  Button,
  Tooltip,
  IconButton,
} from "@mui/material";
import { useEffect, useState } from "react";
import { Loader } from "../../components/CustomLoader";
import { userServices } from "../../services/AllServices";
import errorHandler from "../../utils/errorHandler";
import { capitalize } from "../../utils/helpers";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import PersonIcon from "@mui/icons-material/Person";
import Swal from "sweetalert2";
import { toast } from "react-toastify";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Visibility } from "@mui/icons-material";
import EditableForm from "./EditableForm";
import { userManagementFormConfig } from "./userManagementFormConfig";


const ViewUserManagement = () => {
  const [data, setData] = useState(null);
  const location = useLocation();
  const { id } = useParams();
  const [editMode, setEditMode] = useState(false);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  
  const state = location?.state;

  const userData = async () => {
    try {
      setLoading(true);
      const response = await userServices.getById({ id });
       if(response?.data?.data._id !== id){
        navigate("/user-management")  
       }
      if (state === "edit") {
        setEditMode(true);
      }
      setData(response.data.data);
    } catch (error) {
      errorHandler(error);
      navigate("/user-management");
    } finally {
      setLoading(false);
    }
  };

  const deleteData = async (id) => {
    Swal.fire({
      text: "Are you sure you want to delete this user?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await userServices.deleteUser({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            navigate("/user-management");
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const BlockData = async () => {
    Swal.fire({
      text: `Are you sure you want to ${
        data?.isLocked ? "unblock" : "block"
      } this user?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#00a1da",
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          const res = await userServices.blockUser({ id });
          if (res?.status === 200) {
            toast.success(res?.data?.message, { toastId: "success" });
            userData();
          } else {
            toast.error(res?.data?.message, { toastId: "error" });
          }
        } catch (error) {
          errorHandler(error);
        }
      }
    });
  };

  const updateUserData = async (values) => {
    try {
      const res = await userServices.edit({ id, ...values });
      if (res?.status === 200) {
        toast.success("User updated successfully");
        setEditMode(false);
        userData();
        navigate("/user-management");
      } else {
        toast.error("Failed to update user");
      }
    } catch (error) {
      errorHandler(error);
    }
  };

  useEffect(() => {
    userData();
    
  }, [id]);

  const renderBoolean = (value) => (value ? "Yes" : "No");

  if (loading || !data) return <Loader />;

  return (
    <>
      <Button
        startIcon={<ArrowBackIcon />}
        variant="outlined"
        sx={{ mb: 2, borderRadius: 3 }}
        onClick={() => navigate(-1)}
      >
        Back
      </Button>

      <Card elevation={3} sx={{ borderRadius: 3 }}>
        <Box sx={{ color: "#000000", px: 3, py: 2 }}>
          <Typography variant="h6" fontWeight="bold">
            User Details
          </Typography>
        </Box>
        <CardContent>
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            spacing={1}
            sx={{ mb: 2 }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Avatar
                sx={{ width: 64, height: 64, bgcolor: "#C4CDD5" }}
                src={data?.profileImageUrl || ""}
              >
                {!data?.profileImageUrl && <PersonIcon fontSize="large" />}
              </Avatar>
              <Box>
                <Typography variant="h6" fontWeight="bold">
                  {capitalize(data?.name)}
                </Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1 }}>
              <Tooltip
                title={editMode ? "Cancel Edit" : "Edit"}
                placement="top"
              >
                <IconButton
                  onClick={() => setEditMode((prev) => !prev)}
                  sx={{
                    border: "1px solid #1976d2",
                    color: "#1976d2",
                    borderRadius: 1,
                  }}
                >
                  {editMode ? (
                    <Visibility fontSize="small" />
                  ) : (
                    <EditIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip
                title={data?.isLocked ? "Unblock" : "Block"}
                placement="top"
              >
                <IconButton
                  sx={{
                    color: data?.isLocked ? "green" : "red",
                    border: `1px solid ${data?.isLocked ? "green" : "red"}`,
                    borderRadius: 1,
                  }}
                  onClick={BlockData}
                >
                  {data?.isLocked ? (
                    <LockOpenIcon fontSize="small" />
                  ) : (
                    <LockIcon fontSize="small" />
                  )}
                </IconButton>
              </Tooltip>

              <Tooltip title="Delete" placement="top">
                <IconButton
                  sx={{
                    color: "#6a1b9a",
                    border: "1px solid rgb(13, 13, 14)",
                    borderRadius: 1,
                  }}
                  onClick={() => deleteData(data._id)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          </Stack>

          <Divider sx={{ mb: 2 }} />

                {editMode ? (
                  <EditableForm
                    initialData={userManagementFormConfig.getInitialValues(data)}
                    validationSchema={userManagementFormConfig.validationSchema}
                    onSubmit={updateUserData}
                    fields={userManagementFormConfig.fields}
                    submitButtonText="Save Changes"
                    enableReinitialize={true}
                  />
                ) : (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Address
                      </Typography>
                      <Typography variant="body1">{data?.address}</Typography>
                    </Grid>
      
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Email Address
                      </Typography>
                      <Typography variant="body1">{data?.email}</Typography>
                    </Grid>
      
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Phone Number
                      </Typography>
                      <Typography variant="body1">
                        {data?.countryCode} {data?.phone || "-"}
                      </Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        City
                      </Typography>
                      <Typography variant="body1">{data?.city || "-"}</Typography>
                    </Grid>
      
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Status
                      </Typography>
                      <Chip
                        label={capitalize(data?.status)}
                        color={
                          data?.status === "active"
                            ? "success"
                            : data?.status === "pending"
                            ? "warning"
                            : "default"
                        }
                        variant="outlined"
                        size="small"
                      />
                    </Grid>
      
                    <Grid item xs={6}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Account Locked
                      </Typography>
                      <Typography variant="body1">
                        {renderBoolean(data?.isLocked)}
                      </Typography>
                    </Grid>
      
                    <Grid item xs={12}>
                      <Divider sx={{ my: 2, width: "100%" }} />
                    </Grid>
      
                    {[
                      { label: "Verified", value: data?.isVerified },
                      { label: "Email Verified", value: data?.isEmailVerified },
                      { label: "KYC Verified", value: data?.isKycVerified },
                      { label: "Account Active", value: data?.isActive },
                    ].map((item, i) => (
                      <Grid key={i} item xs={6}>
                        <Typography variant="subtitle2" fontWeight={600}>
                          {item.label}
                        </Typography>
                        <Typography variant="body1">
                          {renderBoolean(item.value)}
                        </Typography>
                      </Grid>
                    ))}
      
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" fontWeight={600}>
                        Created At
                      </Typography>
                      <Typography variant="body2">
                        {new Date(data?.createdAt).toLocaleString()}
                      </Typography>
                    </Grid>
                  </Grid>
                )}
              </CardContent>
            </Card>
</>
)}
export default ViewUserManagement;